import { Users, Target, Eye, Heart } from 'lucide-react';
import SEO from '@/components/SEO';

const About = () => {
  const team = [
    {
      name: 'Bilal Zaidi',
      role: 'Founder & CEO',
      bio: 'Former TV Journalist with Times Now and NDTV. Serial entrepreneur who raised 22 Cr through crowdfunding. His previous startup OurDemocracy achieved 4 Cr revenue in 2 years.',
      image: 'https://api.dicebear.com/7.x/initials/svg?seed=BZ&backgroundColor=e5266a'
    },
    {
      name: 'Mohit Bhardwaj',
      role: 'Head of Growth',
      bio: 'Over a decade of experience managing digital operations for startups. Keeps close eye on tech and marketing trends, helping Elderra maintain frugal operations.',
      image: 'https://api.dicebear.com/7.x/initials/svg?seed=MB&backgroundColor=e5266a'
    },
    {
      name: 'Akshay Bajaj',
      role: 'CTO',
      bio: 'IIT Kanpur Computer Science graduate specializing in Cognitive Behavior. Founding member of three startups, now building Elderra\'s AI product for seniors.',
      image: 'https://api.dicebear.com/7.x/initials/svg?seed=AB&backgroundColor=e5266a'
    },
    {
      name: 'Vaishnavi Ghongade',
      role: 'Founder\'s Office',
      bio: 'Background in law and legal-tech. Supports Elderra\'s core operations, bringing structure, clarity, and sharp problem-solving mindset to ensure smooth execution.',
      image: 'https://api.dicebear.com/7.x/initials/svg?seed=VG&backgroundColor=e5266a'
    }
  ];

  return (
    <div className="page-container">
      <SEO 
        title="About Elderra - Bridging Tech for Elders | Our Mission & Story"
        description="Learn about Elderra's founding team, mission to bridge the digital divide for 150M+ seniors in India. Recognized by Startup India with 1500+ elders trained."
        keywords="about Elderra, bridging tech for elders, digital literacy mission, senior tech training India, Startup India recognized, founding team Elderra"
        ogImage="https://customer-assets.emergentagent.com/job_95459019-fd40-417d-bf1f-43659b4169ce/artifacts/4owwqeky_Elderra%20Banner.png"
        ogUrl="https://elderra.io/about"
      />
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title" data-testid="about-title">About Elderra</h1>
          <p className="page-subtitle">
            Bridging the digital divide for India's senior population
          </p>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="mvv-section">
        <div className="container">
          <div className="mvv-grid">
            <div className="mvv-card">
              <Target className="mvv-icon" size={48} />
              <h3>Our Mission</h3>
              <p>
                ONE WhatsApp-based assistant that aggregates apps and services relevant for elderly population, along with hands-on support.
              </p>
            </div>
            <div className="mvv-card">
              <Eye className="mvv-icon" size={48} />
              <h3>Our Vision</h3>
              <p>
                Making the internet simpler and accessible for the elderly population. Our parents should be able to manage their everyday lives even when we are living elsewhere or travelling.
              </p>
            </div>
            <div className="mvv-card">
              <Heart className="mvv-icon" size={48} />
              <h3>Our Values</h3>
              <p>
                Accessibility, Simplicity, Empowerment, Support, Community, Trust & Security, and Innovation for the elderly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Launch Now */}
      <section className="why-now-section">
        <div className="container">
          <h2 className="section-title-center">Why Launch Now?</h2>
          <div className="why-now-grid">
            <div className="why-now-card">
              <div className="why-now-number">01</div>
              <h3>Demographic Shifts</h3>
              <p>Young people moving for work, leaving elders behind. The nuclear family structure is creating new challenges.</p>
            </div>
            <div className="why-now-card">
              <div className="why-now-number">02</div>
              <h3>Longer Lifespans</h3>
              <p>Better healthcare access is leading to longer lifespans, requiring digital literacy for extended independent living.</p>
            </div>
            <div className="why-now-card">
              <div className="why-now-number">03</div>
              <h3>Rapid Tech Evolution</h3>
              <p>Technology is evolving rapidly and has disrupted traditional lifestyle, creating an urgent need for adaptation.</p>
            </div>
            <div className="why-now-card">
              <div className="why-now-number">04</div>
              <h3>Cognitive Health Advances</h3>
              <p>Advances in cognitive health mean retirement isn't always necessary. Digital skills unlock new opportunities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <h2 className="section-title-center">Our Founding Team</h2>
          <p className="section-subtitle-center">
            Together, we drive Elderra's success and vision
          </p>
          <div className="team-grid">
            {team.map((member, index) => (
              <div key={index} className="team-card" data-testid={`team-member-${index}`}>
                <img src={member.image} alt={member.name} className="team-image" />
                <h3 className="team-name">{member.name}</h3>
                <p className="team-role">{member.role}</p>
                <p className="team-bio">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey So Far */}
      <section className="journey-section">
        <div className="container">
          <h2 className="section-title-center">Journey So Far</h2>
          <div className="journey-stats">
            <div className="journey-stat">
              <div className="journey-stat-number">1,500+</div>
              <div className="journey-stat-label">Paid Users</div>
            </div>
            <div className="journey-stat">
              <div className="journey-stat-number">50+</div>
              <div className="journey-stat-label">Local WhatsApp Groups</div>
            </div>
            <div className="journey-stat">
              <div className="journey-stat-number">100+</div>
              <div className="journey-stat-label">Societies & Clubs Targeted</div>
            </div>
            <div className="journey-stat">
              <div className="journey-stat-number">20+</div>
              <div className="journey-stat-label">Elder-Focused Products Onboarded</div>
            </div>
          </div>
        </div>
      </section>

      {/* Recognized By - Trust Badges Strip */}
      <section className="trust-badges-section" data-testid="trust-badges-section">
        <div className="container">
          <h2 className="section-title-center">Recognized By</h2>
          <p className="section-subtitle-center">
            Backed by India's innovation ecosystem and covered by national media
          </p>
          <div className="trust-badges-grid">
            <a
              href="https://www.startupindia.gov.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="trust-badge-link"
              data-testid="trust-badge-startup-india"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/qvp8avyl_png-transparent-government-of-india-startup-india-startup-company-entrepreneurship-india-company-text-orange-thumbnail.png"
                alt="Startup India"
                className="trust-badge-logo"
                loading="lazy"
              />
            </a>
            <a
              href="https://nsrcel.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="trust-badge-link"
              data-testid="trust-badge-nsrcel"
            >
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_95459019-fd40-417d-bf1f-43659b4169ce/artifacts/igzdcpvl_NSRCEL%20Main%20logo%20%282%29.webp"
                alt="IIM Bangalore · NSRCEL"
                className="trust-badge-logo"
                loading="lazy"
              />
            </a>
            <a
              href="https://www.thehindu.com/news/cities/bangalore/in-the-age-of-age-tech-how-bengalurus-elderly-are-getting-empowered/article69430340.ece"
              target="_blank"
              rel="noopener noreferrer"
              className="trust-badge-link"
              data-testid="trust-badge-the-hindu"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_95459019-fd40-417d-bf1f-43659b4169ce/artifacts/t94zvruy_the-hindu-logo.png"
                alt="The Hindu"
                className="trust-badge-logo"
                loading="lazy"
              />
            </a>
            <a
              href="https://www.instagram.com/p/DQy7un7iCF-/"
              target="_blank"
              rel="noopener noreferrer"
              className="trust-badge-link"
              data-testid="trust-badge-better-india"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_95459019-fd40-417d-bf1f-43659b4169ce/artifacts/qs2r9pbc_The_Better_India_id4voN7PrB_0.png"
                alt="The Better India"
                className="trust-badge-logo"
                loading="lazy"
              />
            </a>
            <a
              href="https://thefederal.com/category/news/digital-arrest-scam-victims-law-enforcement-failure-226061"
              target="_blank"
              rel="noopener noreferrer"
              className="trust-badge-link"
              data-testid="trust-badge-the-federal"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_95459019-fd40-417d-bf1f-43659b4169ce/artifacts/b6xly8cy_The%20federal%20logo.png"
                alt="The Federal"
                className="trust-badge-logo"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;